import type { DailyRoutine, RoutineStats, RoutineDifficulty } from '../types/routine';
import type { RoutineIntensity, ExerciseType } from '../types/routine';
import { APP_CONFIG } from '../../shared/config/app.config';
import { AuthService } from '../../auth/services/authService';
import { esFalloDeRed, esJsonInvalido } from '../../shared/utils/red';

const API_BASE_URL = APP_CONFIG.API.BASE_URL;

function authHeaders(): HeadersInit {
  const token = AuthService.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** `?difficulty=` solo si hay nivel; sin él la API sirve intermediate. */
function difficultyQuery(difficulty?: RoutineDifficulty): string {
  return difficulty ? `?difficulty=${difficulty}` : '';
}

/** Un ejercicio tal y como lo espera la API al guardar una rutina. */
export interface RoutineExercisePayload {
  exerciseId: string;
  orderInRoutine: number;
  exerciseType?: ExerciseType;
  sets?: number;
  reps?: number;
  weight?: number;
  durationSeconds?: number;
  distanceMeters?: number;
  restSeconds?: number;
  notes?: string;
}

/**
 * Lo que el panel de monitores puede cambiar de un día.
 *
 * No están `dayOfWeek` ni `difficulty` a propósito: el backend los rechaza
 * (`forbidNonWhitelisted`) y son justo lo que hace que la fila sea la
 * plantilla de ese día.
 */
export interface WeeklyDayPayload {
  name?: string;
  description?: string;
  intensity?: RoutineIntensity;
  estimatedDurationMinutes?: number;
  estimatedCalories?: number;
  goals?: string[];
  warmUpNotes?: string;
  coolDownNotes?: string;
  exercises?: RoutineExercisePayload[];
}

export class RoutineService {
  static async getTodayRoutine(
    difficulty?: RoutineDifficulty,
  ): Promise<DailyRoutine | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/routines/daily/today${difficultyQuery(difficulty)}`,
        { headers: authHeaders() },
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          return null; // No hay rutina para hoy
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Verificar que la respuesta tiene formato válido
      if (!data || typeof data !== 'object') {
        console.warn('Respuesta inesperada del servidor para rutina de hoy:', data);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching today routine:', error);
      
      // Si es un error de red, no lanzar error sino retornar null
      if (esFalloDeRed(error)) {
        console.warn('Error de conexión, no hay rutina de hoy disponible');
        return null;
      }
      
      // Si es un error de parsing JSON (respuesta vacía), retornar null
      if (esJsonInvalido(error)) {
        console.log('Respuesta vacía del servidor, no hay rutina para hoy');
        return null;
      }
      
      throw new Error('Error al obtener la rutina de hoy');
    }
  }

  /**
   * Obtener rutina por fecha específica
   */
  static async getRoutineByDate(
    date: string,
    difficulty?: RoutineDifficulty,
  ): Promise<DailyRoutine | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/routines/daily/by-date/${date}${difficultyQuery(difficulty)}`,
        { headers: authHeaders() },
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Verificar que la respuesta tiene formato válido
      if (!data || typeof data !== 'object') {
        console.warn('Respuesta inesperada del servidor para rutina por fecha:', data);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching routine by date:', error);
      
      // Si es un error de red, no lanzar error sino retornar null
      if (esFalloDeRed(error)) {
        console.warn(`Error de conexión, no hay rutina disponible para ${date}`);
        return null;
      }
      
      // Si es un error de parsing JSON (respuesta vacía), retornar null
      if (esJsonInvalido(error)) {
        console.log(`Respuesta vacía del servidor, no hay rutina para ${date}`);
        return null;
      }
      
      throw new Error(`Error al obtener la rutina para ${date}`);
    }
  }

  /**
   * La rutina del gimnasio para un dia de la semana (0 domingo … 6 sabado).
   *
   * Desde que las rutinas son fijas, cada lunes es el mismo lunes: elegir una
   * fecha para verla no significaba nada. Se pide el dia.
   */
  static async getRoutineByWeekday(
    dayOfWeek: number,
    difficulty?: RoutineDifficulty,
  ): Promise<DailyRoutine | null> {
    const response = await fetch(
      `${API_BASE_URL}/routines/daily/by-weekday/${dayOfWeek}${difficultyQuery(difficulty)}`,
      { headers: authHeaders() },
    );

    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()) as DailyRoutine | null;
    return data && typeof data === 'object' ? data : null;
  }

  /**
   * Obtener todas las rutinas
   */
  static async getAllRoutines(): Promise<DailyRoutine[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/routines/daily`, {
        headers: authHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          return []; // Retorna array vacío si no hay rutinas
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Verificar que data es un array, si no, retornar array vacío
      if (!Array.isArray(data)) {
        console.warn('Respuesta inesperada del servidor:', data);
        return [];
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching all routines:', error);
      
      // Si es un error de red o de respuesta, retornar array vacío en lugar de lanzar error
      if (esFalloDeRed(error)) {
        console.warn('Error de conexión, retornando array vacío');
        return [];
      }
      
      throw new Error('Error al obtener las rutinas');
    }
  }

  /**
   * Obtener rutina por ID
   */
  static async getRoutineById(id: string): Promise<DailyRoutine | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/routines/daily/${id}`, {
        headers: authHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching routine by ID:', error);
      throw new Error(`Error al obtener la rutina ${id}`);
    }
  }

  /**
   * Calcular estadísticas de una rutina
   */
  static calculateRoutineStats(routine: DailyRoutine): RoutineStats {
    const exercises = routine.routineExercises || [];
    
    // Extraer grupos musculares únicos
    const muscleGroups = new Set<string>();
    exercises.forEach(exercise => {
      // Usar muscleGroups que es la estructura real del backend
      if (exercise.exercise.muscleGroups && Array.isArray(exercise.exercise.muscleGroups)) {
        exercise.exercise.muscleGroups.forEach((group: string) => muscleGroups.add(group));
      }
    });

    return {
      totalExercises: exercises.length,
      estimatedDuration: routine.estimatedDurationMinutes || 60,
      estimatedCalories: routine.estimatedCalories || 300,
      muscleGroups: Array.from(muscleGroups),
    };
  }

  /**
   * Formatear duración en minutos a texto legible
   */
  static formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    
    return `${hours}h ${remainingMinutes}min`;
  }

  /**
   * Obtener el nombre del día en español
   */
  static getDayName(date: Date): string {
    const days = [
      'Domingo', 'Lunes', 'Martes', 'Miércoles', 
      'Jueves', 'Viernes', 'Sábado'
    ];
    return days[date.getDay()];
  }

  /**
   * Formatear fecha para mostrar
   */
  static formatDate(dateString: string): string {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Comparar solo las fechas (sin hora)
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const tomorrowOnly = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());

    if (dateOnly.getTime() === todayOnly.getTime()) {
      return 'Hoy';
    } else if (dateOnly.getTime() === tomorrowOnly.getTime()) {
      return 'Mañana';
    } else {
      // Usar barras en lugar de puntos para las fechas
      return `${this.getDayName(date)} ${date.getDate()}/${date.getMonth() + 1}`;
    }
  }

  /**
   * Obtener el color de intensidad
   */
  static getIntensityColor(intensity: string): string {
    switch (intensity) {
      case 'low': return 'text-green-400';
      case 'moderate': return 'text-yellow-400';
      case 'high': return 'text-orange-400';
      case 'very_high': return 'text-red-400';
      default: return 'text-gray-400';
    }
  }

  /**
   * Obtener el color de estado
   */
  static getStatusColor(status: string): string {
    switch (status) {
      case 'planned': return 'text-blue-400';
      case 'in_progress': return 'text-yellow-400';
      case 'completed': return 'text-green-400';
      case 'skipped': return 'text-gray-400';
      default: return 'text-gray-400';
    }
  }

  /**
   * Traducir textos de intensidad
   */
  static translateIntensity(intensity: string): string {
    switch (intensity) {
      case 'low': return 'Baja';
      case 'moderate': return 'Moderada';
      case 'high': return 'Alta';
      case 'very_high': return 'Muy Alta';
      default: return intensity;
    }
  }

  /**
   * Traducir textos de estado
   */
  static translateStatus(status: string): string {
    switch (status) {
      case 'planned': return 'Planificada';
      case 'in_progress': return 'En Progreso';
      case 'completed': return 'Completada';
      case 'skipped': return 'Omitida';
      default: return status;
    }
  }

  static async listMyRoutines(): Promise<DailyRoutine[]> {
    const res = await fetch(`${API_BASE_URL}/routines/mine`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json() as Promise<DailyRoutine[]>;
  }

  static async getMyRoutine(id: string): Promise<DailyRoutine> {
    const res = await fetch(`${API_BASE_URL}/routines/mine/${id}`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json() as Promise<DailyRoutine>;
  }

  static async cloneRoutineToMine(sourceId: string): Promise<DailyRoutine> {
    const res = await fetch(`${API_BASE_URL}/routines/${sourceId}/clone`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? `HTTP ${res.status}`);
    }
    return res.json() as Promise<DailyRoutine>;
  }

  static async deleteMyRoutine(id: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/routines/mine/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
  }

  static async importFromFile(file: File): Promise<{
    name: string;
    description?: string;
    exercises: {
      exerciseId: string | null;
      rawName: string;
      matchedName?: string | null;
      orderInRoutine: number;
      sets?: number;
      reps?: number;
      weight?: number;
      durationSeconds?: number;
      distanceMeters?: number;
      restSeconds?: number;
      notes?: string;
    }[];
  }> {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_BASE_URL}/routines/import`, {
      method: 'POST',
      headers: authHeaders(),
      body: form,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? `HTTP ${res.status}`);
    }
    return res.json();
  }

  static async listPublicRoutines(): Promise<DailyRoutine[]> {
    const res = await fetch(`${API_BASE_URL}/routines/public`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json() as Promise<DailyRoutine[]>;
  }

  static async updateMyRoutine(
    id: string,
    payload: Partial<{
      name: string;
      description?: string;
      intensity: RoutineIntensity;
      estimatedDurationMinutes: number;
      goals: string[];
      visibility: 'private' | 'public';
      /** Dia de la semana en el que colocarla; `null` la deja sin colocar. */
      dayOfWeek: number | null;
      exercises: {
        exerciseId: string;
        orderInRoutine: number;
        exerciseType?: ExerciseType;
        sets?: number;
        reps?: number;
        weight?: number;
        durationSeconds?: number;
        distanceMeters?: number;
        restSeconds?: number;
        notes?: string;
      }[];
    }>,
  ): Promise<DailyRoutine> {
    const res = await fetch(`${API_BASE_URL}/routines/mine/${id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? `HTTP ${res.status}`);
    }
    return res.json() as Promise<DailyRoutine>;
  }

  /**
   * Las siete rutinas fijas de la semana, de domingo a sábado.
   * Solo para monitores y administradores: el backend devuelve 403 al resto.
   */
  static async getWeeklyPlan(): Promise<DailyRoutine[]> {
    const res = await fetch(`${API_BASE_URL}/routines/weekly-plan`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json() as Promise<DailyRoutine[]>;
  }

  /**
   * Cambia la rutina fija de un día (0 domingo … 6 sábado).
   *
   * Los metadatos se parchean; `exercises`, si viene, sustituye la lista
   * entera. El cambio vale para ese día de la semana de aquí en adelante:
   * los entrenos ya hechos guardan su propia copia.
   */
  static async updateWeeklyDay(
    dayOfWeek: number,
    payload: WeeklyDayPayload,
  ): Promise<DailyRoutine> {
    const res = await fetch(
      `${API_BASE_URL}/routines/weekly-plan/${dayOfWeek}`,
      {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? `HTTP ${res.status}`);
    }
    return res.json() as Promise<DailyRoutine>;
  }

  static async createMyRoutine(payload: {
    name: string;
    description?: string;
    intensity?: RoutineIntensity;
    estimatedDurationMinutes?: number;
    goals?: string[];
    dayOfWeek?: number | null;
    exercises: {
      exerciseId: string;
      orderInRoutine: number;
      exerciseType?: ExerciseType;
      sets?: number;
      reps?: number;
      weight?: number;
      durationSeconds?: number;
      distanceMeters?: number;
      restSeconds?: number;
      notes?: string;
    }[];
  }): Promise<DailyRoutine> {
    const res = await fetch(`${API_BASE_URL}/routines/mine`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? `HTTP ${res.status}`);
    }
    return res.json() as Promise<DailyRoutine>;
  }
}
